import * as History from 'history';
import { WebFrameFilters } from '../interfaces/WebFrameFilters';
import { FilterTag } from '..';

export const getQueryParams = (location: any) => {
  const queryParams = new URLSearchParams(location.search);
  return Object.fromEntries(queryParams.entries());
};

export const convertFilterParamsToLowerCase = (params: WebFrameFilters) => {
  const newParams: WebFrameFilters = {};

  Object.keys(params).forEach((k) => {
    newParams[k.toLowerCase()] = params[k].toLowerCase();
  });
  return newParams;
};

export const compareQueryParams = (
  currentParams: WebFrameFilters,
  newParams: WebFrameFilters
): boolean => {
  if (!currentParams) {
    return true;
  }
  const currentFilterParams = convertFilterParamsToLowerCase(currentParams);
  const newFilterParams = convertFilterParamsToLowerCase(newParams);
  const isEqual =
    JSON.stringify(currentFilterParams) === JSON.stringify(newFilterParams);

  return isEqual;
};

export interface KnownFilterQuery {
  key: string;
  alias: string[];
}

export interface NavigationFilter {
  id: string;
  name: string;
}

export enum FilterKeys {
  Client = 0,
  Region,
  Country,
}

export const CLIENT_FILTER_KEY = 'client';
export const CLIENT_ID_FILTER_KEY = 'clientId';
export const REGION_FILTER_KEY = 'level1';
export const REGION_ID_FILTER_KEY = 'regionId';
export const COUNTRY_FILTER_KEY = 'level2';
export const COUNTRY_ID_FILTER_KEY = 'countryId';

const knownFilterQueries: KnownFilterQuery[] = [
  {
    key: CLIENT_FILTER_KEY,
    alias: ['client'],
  },
  {
    key: REGION_FILTER_KEY,
    alias: ['level1', 'region'],
  },
  {
    key: COUNTRY_FILTER_KEY,
    alias: ['level2', 'country'],
  },
];

// The additional parameter can be used to retrieve additional filter tags from the query
// This may be used for example when saving a brief where the "brand" needs to be saved as well
// however, we don't need sorting for "brand" at other places, so it can be handled through
// the last parameter
export const constructFilterTagsFromQuery = (
  location: History.Location,
  additional: KnownFilterQuery[] = []
) => {
  const query = new URLSearchParams(location.search);
  const filterTags: FilterTag[] = [];
  const filters = knownFilterQueries.concat(additional);

  filters.forEach((filter) => {
    filter.alias.forEach((alias) => {
      const filterValue = query.get(alias);
      if (filterValue && !filterTags.find((x) => x.key === filter.key)) {
        filterTags.push({ key: filter.key, value: filterValue });
      }
    });
  });
  return filterTags;
};

const emptyFilter = { id: '', name: '' };

export const constructFilterTagsFromObservable = (
  selectedFilters: NavigationFilter[],
  useIds: boolean
) => {
  const filterTags: FilterTag[] = [];
  const clientFilter = selectedFilters[FilterKeys.Client] ?? emptyFilter;
  const regionFilter = selectedFilters[FilterKeys.Region] ?? emptyFilter;
  const countryFilter = selectedFilters[FilterKeys.Country] ?? emptyFilter;

  if (useIds) {
    filterTags.push(
      {
        key: CLIENT_ID_FILTER_KEY,
        value: clientFilter.id,
      },
      {
        key: REGION_ID_FILTER_KEY,
        value: regionFilter.id,
      },
      {
        key: COUNTRY_ID_FILTER_KEY,
        value: countryFilter.id,
      }
    );
  } else {
    filterTags.push(
      {
        key: CLIENT_FILTER_KEY,
        value: clientFilter.name,
      },
      {
        key: REGION_FILTER_KEY,
        value: regionFilter.name,
      },
      {
        key: COUNTRY_FILTER_KEY,
        value: countryFilter.name,
      }
    );
  }

  return filterTags;
};

export const handleAndEncodeQueryParameter = (parameter: string) => {
  parameter = handleSpecialCharacters(parameter);
  return encodeURIComponent(parameter);
};

export const handleSpecialCharacters = (parameter: string) =>
  parameter.replaceAll("'", "''");
